import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';

import type { ApiCategory, ApiCity, ApiProvider } from '../../core/api/api.types';
import { AuthService } from '../../core/auth/auth.service';
import { ProvidersApiService } from '../../core/api/providers-api.service';
import { ReferenceApiService } from '../../core/api/reference-api.service';
import { CanadaPhoneMaskDirective } from '../../shared/directives/canada-phone-mask.directive';
import {
  WorkingHoursInputComponent,
  DEFAULT_WORKING_HOURS,
  type WorkingHours,
} from '../../shared/components/working-hours-input/working-hours-input.component';

function requiredNonEmptyArray(control: AbstractControl) {
  return Array.isArray(control.value) && control.value.length > 0 ? null : { required: true };
}

@Component({
  selector: 'op-create-provider-page',
  imports: [
    RouterModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltipModule,
    CanadaPhoneMaskDirective,
    WorkingHoursInputComponent,
  ],
  templateUrl: './create-provider.page.html',
  styleUrl: './create-provider.page.scss',
})
export class CreateProviderPage implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly providersApi = inject(ProvidersApiService);
  private readonly referenceApi = inject(ReferenceApiService);

  readonly isSuperAdmin = computed(() => this.auth.user()?.role === 'SUPERADMIN');

  /** Set when the page is opened on the `/providers/:id/edit` route. */
  readonly editId = signal<string | null>(null);
  readonly isEditMode = computed(() => !!this.editId());

  readonly cities = signal<ApiCity[]>([]);
  readonly categories = signal<ApiCategory[]>([]);
  readonly refLoading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly saved = signal(false);

  // ── Stepper ──────────────────────────────────────────────────────────────
  readonly step = signal(0);

  readonly STEPS = [
    { label: 'Basic Info',    icon: 'person'         },
    { label: 'Verification',  icon: 'verified_user'  },
    { label: 'Coverage',      icon: 'location_on'    },
    { label: 'Notes',         icon: 'notes'          },
  ];

  readonly isLastStep = computed(() => this.step() === this.STEPS.length - 1);

  canGoNext = computed((): boolean => {
    this.formStatus(); // bridge RxJS form validity into signal graph
    const c = this.form.controls;
    switch (this.step()) {
      case 0: return c.name.valid && c.email.valid && c.languages.valid;
      case 2: return c.city_codes.valid && c.category_ids.valid;
      default: return true;
    }
  });

  readonly formInvalid = computed((): boolean => {
    this.formStatus();
    return this.form.invalid;
  });

  next(): void {
    const step = this.step();
    const c = this.form.controls;

    if (step === 0) {
      c.name.markAsTouched();
      c.email.markAsTouched();
      c.languages.markAsTouched();
      if (!(c.name.valid && c.email.valid && c.languages.valid)) return;
    }

    if (step === 2) {
      c.city_codes.markAsTouched();
      c.category_ids.markAsTouched();
      if (!(c.city_codes.valid && c.category_ids.valid)) return;
    }

    if (!this.isLastStep()) this.step.update(s => s + 1);
  }

  prev(): void {
    if (this.step() > 0) this.step.update(s => s - 1);
  }

  // ── Categories grouped by vertical ───────────────────────────────────────
  readonly categoriesByVertical = computed(() => {
    const groups: Record<string, ApiCategory[]> = {};
    for (const cat of this.categories()) {
      const key = cat.vertical || 'Other';
      (groups[key] ??= []).push(cat);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  });

  // ── Review summary (step 4) ───────────────────────────────────────────────
  readonly reviewSummary = computed(() => {
    const v = this.form.getRawValue();
    const catNames = v.category_ids
      .map(id => this.categories().find(c => c.category_id === id)?.name ?? id)
      .join(', ') || '—';
    const cityLabel = v.city_codes.length
      ? v.city_codes.map(code => this.cities().find(c => c.code === code)?.label ?? code).join(', ')
      : 'All cities';
    const langs = v.languages
      .map(l => this.LANGUAGE_OPTIONS.find(o => o.value === l)?.label ?? l)
      .join(', ') || '—';
    return [
      { label: 'Name',       value: v.name || '—' },
      { label: 'Contact',    value: v.contact_name || '—' },
      { label: 'Phone',      value: [v.phone, v.secondary_phone].filter(Boolean).join(' / ') || '—' },
      { label: 'Languages',  value: langs },
      { label: 'Insurance',  value: v.insurance_verified ? 'Verified' : 'Not verified' },
      { label: 'WCB',        value: v.wcb_registered ? 'Registered' : 'Not registered' },
      { label: 'Cities',     value: cityLabel },
      { label: 'Categories', value: catNames },
    ];
  });

  // ── Vertical label mapper ─────────────────────────────────────────────────
  private readonly VERTICAL_LABEL_MAP: Record<string, string> = {
    auto:     'Auto',
    beauty:   'Beauty',
    cleaning: 'Cleaning',
    home:     'Home',
  };

  verticalLabel(key: string): string {
    return (
      this.VERTICAL_LABEL_MAP[key] ??
      key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    );
  }

  // ── Static options ────────────────────────────────────────────────────────
  readonly CONTACT_METHODS = [
    { value: 'phone',    label: 'Phone call' },
    { value: 'sms',      label: 'SMS'        },
    { value: 'email',    label: 'Email'      },
    { value: 'whatsapp', label: 'WhatsApp'   },
  ];

  readonly TRUST_STATUS_OPTIONS = [
    { value: 'trusted',   label: 'Trusted',    icon: '✅' },
    { value: 'caution',   label: 'Caution',    icon: '⚠️' },
    { value: 'pre_block', label: 'Pre-Block',  icon: '‼️' },
    { value: 'blocked',   label: 'Blocked',    icon: '❌' },
  ];

  readonly LANGUAGE_OPTIONS = [
    { value: 'en', label: 'English'    },
    { value: 'uk', label: 'Ukrainian'  },
    { value: 'hi', label: 'Hindi'      },
    { value: 'fr', label: 'French'     },
  ];

  // ── Form ─────────────────────────────────────────────────────────────────
  readonly form = this.fb.nonNullable.group({
    name:                     ['', [Validators.required, Validators.maxLength(200)]],
    contact_name:             ['', [Validators.maxLength(200)]],
    phone:                    ['', [Validators.maxLength(32)]],
    secondary_phone:          ['', [Validators.maxLength(32)]],
    email:                    ['', [Validators.required, Validators.email, Validators.maxLength(200)]],
    preferred_contact_method: ['phone'],
    languages:                [['en'] as string[], [Validators.required]],
    insurance_verified:       [false],
    wcb_registered:           [false],
    city_codes:               [[] as string[], [requiredNonEmptyArray]],
    working_hours:            [DEFAULT_WORKING_HOURS as WorkingHours],
    category_ids:             [[] as string[], [requiredNonEmptyArray]],
    internal_notes:           ['', [Validators.maxLength(2000)]],
    admin_notes:              ['', [Validators.maxLength(2000)]],
    trust_status:             ['trusted' as string],
    activate_provider:        [false],
  });

  private readonly formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });

  async ngOnInit(): Promise<void> {
    try {
      const [citiesRes, categoriesRes] = await Promise.all([
        this.referenceApi.cities(),
        this.referenceApi.categories(),
      ]);
      this.cities.set(citiesRes.items);
      this.categories.set(categoriesRes.items);
    } catch (err) {
      console.warn('[CreateProvider] failed to load reference data', err);
    } finally {
      this.refLoading.set(false);
    }

    // Edit mode: `/providers/:id/edit` — load the provider and prefill the form.
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.editId.set(id);
      try {
        const provider = await this.providersApi.getById(id);
        this.prefill(provider);
      } catch (err) {
        console.warn('[CreateProvider] failed to load provider for editing', err);
        this.error.set('Failed to load provider for editing.');
      }
    }
  }

  private prefill(p: ApiProvider): void {
    this.form.patchValue({
      name: p.name ?? '',
      contact_name: p.contact_name ?? '',
      phone: p.phone ?? '',
      secondary_phone: p.secondary_phone ?? '',
      email: p.email ?? '',
      preferred_contact_method: p.preferred_contact_method || 'phone',
      languages: p.languages?.length ? p.languages : ['en'],
      insurance_verified: p.insurance_verified,
      wcb_registered: p.wcb_registered,
      city_codes: p.city_codes ?? [],
      working_hours: (p.working_hours ?? DEFAULT_WORKING_HOURS) as WorkingHours,
      category_ids: p.category_ids ?? [],
      internal_notes: p.internal_notes ?? '',
      admin_notes: p.admin_notes ?? '',
      trust_status: p.trust_status ?? 'trusted',
      activate_provider: p.is_active,
    });
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.saving()) return;
    this.saving.set(true);
    this.error.set(null);
    const v = this.form.getRawValue();
    const payload = {
      name:                     v.name.trim(),
      contact_name:             v.contact_name?.trim() || null,
      phone:                    v.phone?.trim() || null,
      secondary_phone:          v.secondary_phone?.trim() || null,
      email:                    v.email?.trim() || null,
      city_codes:               v.city_codes,
      working_hours:            v.working_hours,
      preferred_contact_method: v.preferred_contact_method || 'phone',
      internal_notes:           v.internal_notes?.trim() || null,
      admin_notes:              v.admin_notes?.trim() || null,
      languages:                v.languages.length ? v.languages : ['en'],
      insurance_verified:       v.insurance_verified,
      wcb_registered:           v.wcb_registered,
      trust_status:             (v.trust_status || null) as 'trusted' | 'caution' | 'pre_block' | 'blocked' | null,
      category_ids:             v.category_ids,
      is_active:                v.activate_provider,
    };
    try {
      const editId = this.editId();
      if (editId) {
        await this.providersApi.update(editId, payload);
      } else {
        await this.providersApi.create(payload);
      }
      this.saved.set(true);
    } catch (err: any) {
      const verb = this.isEditMode() ? 'update' : 'create';
      this.error.set(err?.message ?? `Failed to ${verb} provider. Please try again.`);
    } finally {
      this.saving.set(false);
    }
  }

  addAnother(): void {
    this.form.reset({
      name: '', contact_name: '', phone: '', secondary_phone: '', email: '',
      preferred_contact_method: 'phone', languages: ['en'],
      insurance_verified: false, wcb_registered: false,
      city_codes: [], working_hours: DEFAULT_WORKING_HOURS, category_ids: [], internal_notes: '', admin_notes: '',
      trust_status: 'trusted', activate_provider: false,
    });
    this.form.markAsUntouched();
    this.step.set(0);
    this.saved.set(false);
    this.error.set(null);
  }
}